import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  CalendarDays, UserCog, Users, KeyRound, PackageCheck, ClipboardList, MessageSquareWarning, Shirt, Car, Route, Radio,
  Calculator, ShieldCheck, BookOpenText, ClipboardCheck, MessageSquare, FileSignature, FolderOpen, Nfc, ShieldAlert,
  Truck, ScrollText, Palmtree, Target, Receipt, BellRing, Gavel, FileSpreadsheet, Bot, Mail, Building2, HardHat, Briefcase, ArrowLeftRight,
} from "lucide-react";

const E = "exploitation";
const S = "secretariat";
const EXP_KEYS = new Set(["planning", "agents", "clients", "suivi_cles", "cles_envoyees", "consignes", "dotations", "vehicules", "carnet_bord", "presence", "passation", "suivi_gardiennage", "points_nfc", "pti", "rondes", "suivi_rondes", "soustraitants", "comptes_clients", "suivi"]);
const mgr = (c) => c.isManager;
const sec = (c) => c.isManager && c.secOk;
const tab = (k) => (c) => c.canTab(k);
const adm = (c) => c.isAdmin;
const am = (c) => c.isAdmin || c.role === "manager";

const TILES = [
  { key: "planning", label: "Planning", icon: CalendarDays, to: "/dashboard/planning", def: E, show: tab("planning") },
  { key: "agents", label: "Agents", icon: UserCog, to: "/dashboard/agents", def: E, show: tab("agents") },
  { key: "clients", label: "Clients", icon: Building2, to: "/dashboard/clients", def: E, show: tab("clients") },
  { key: "suivi_cles", label: "Suivi des clés", icon: KeyRound, to: "/dashboard/suivi-cles", def: E, show: tab("suivi_cles") },
  { key: "cles_envoyees", label: "Clés envoyées", icon: PackageCheck, to: "/dashboard/cles-envoyees", def: E, show: tab("cles_envoyees") },
  { key: "consignes", label: "Consignes du jour", icon: ClipboardList, to: "/dashboard/consignes", def: E, show: tab("consignes") },
  { key: "reclamations", label: "Réclamations", icon: MessageSquareWarning, to: "/dashboard/reclamations", def: E, show: sec },
  { key: "dotations", label: "Dotation", icon: Shirt, to: "/dashboard/dotations", def: E, show: mgr },
  { key: "vehicules", label: "Véhicules", icon: Car, to: "/dashboard/vehicules", def: E, show: mgr },
  { key: "carnet_bord", label: "Carnet de bord", icon: Route, to: "/dashboard/carnet-de-bord-suivi", def: E, show: mgr },
  { key: "presence", label: "Présence agent", icon: Radio, to: "/dashboard/presence", def: E, show: tab("presence") },
  { key: "decompte_paie", label: "Décompte paie", icon: Calculator, to: "/dashboard/decompte-paie", def: E, group: "Paie", show: tab("decompte_paie") },
  { key: "paie_ccn", label: "Paie CCN", icon: Calculator, to: "/dashboard/paie-ccn", def: E, group: "Paie", show: tab("paie_ccn") },
  { key: "managers", label: "Managers", icon: ShieldCheck, to: "/dashboard/managers", def: S, show: adm },
  { key: "passation", label: "Passation", icon: BookOpenText, to: "/dashboard/passation", def: S, show: tab("passation") },
  { key: "compte_rendu", label: "Compte rendu", icon: ClipboardCheck, to: "/dashboard/comptes-rendus-interventions", def: S, show: tab("cr_interventions") },
  { key: "cr_gardiennage", label: "Comptes rendus gardiennage", icon: ShieldCheck, to: "/dashboard/comptes-rendus-gardiennage", def: S, show: tab("cr_gardiennage") },
  { key: "cr_rondes", label: "Comptes rendus rondes", icon: Route, to: "/dashboard/comptes-rendus-rondes", def: S, show: tab("cr_interventions") },
  { key: "suivi_gardiennage", label: "Suivi gardiennage", icon: Radio, to: "/dashboard/suivi-gardiennage", def: S, show: tab("suivi_gardiennage") },
  { key: "messagerie", label: "Messagerie", icon: MessageSquare, to: "/dashboard/messages", def: S, show: () => true },
  { key: "appels_offres", label: "Appels d'offres", icon: FileSignature, to: "/dashboard/appels-offres", def: S, show: mgr },
  { key: "documents_societe", label: "Documents société", icon: FolderOpen, to: "/dashboard/documents-societe", def: S, show: sec },
  { key: "conges", label: "Congés", icon: Palmtree, to: "/dashboard/conges", def: S, show: tab("conges") },
  { key: "devis", label: "Devis", icon: ClipboardList, to: "/dashboard/devis", def: S, show: tab("devis") },
  { key: "prospection", label: "Prospection", icon: Target, to: "/dashboard/prospection", def: S, show: tab("prospection") },
  { key: "notes_frais", label: "Notes de frais", icon: Receipt, to: "/dashboard/notes-frais", def: S, show: tab("notes_frais") },
  { key: "relances", label: "Relances", icon: BellRing, to: "/dashboard/relances", def: S, show: tab("relances") },
  { key: "amendes", label: "Amendes", icon: Gavel, to: "/dashboard/amendes", def: S, show: sec },
  { key: "controle_devis", label: "Contrôle devis", icon: ClipboardCheck, to: "/dashboard/controle-devis", def: S, show: tab("controle_devis") },
  { key: "controle_facture", label: "Contrôle facture", icon: FileSpreadsheet, to: "/dashboard/controle-facture", def: S, show: tab("controle_facture") },
  { key: "facturation_gardiennage", label: "Facturation gardiennage", icon: Calculator, to: "/dashboard/facturation-gardiennage", def: S, show: tab("facturation_gardiennage") },
  { key: "points_nfc", label: "Points NFC", icon: Nfc, to: "/dashboard/points-nfc", def: S, show: am },
  { key: "pti", label: "PTI", icon: ShieldAlert, to: "/dashboard/pti", def: S, show: am },
  { key: "rondes", label: "Rondes", icon: Route, to: "/dashboard/rondes", def: S, show: am },
  { key: "suivi_rondes", label: "Suivi des rondes", icon: Radio, to: "/dashboard/suivi-rondes", def: S, show: am },
  { key: "soustraitants", label: "Sous-traitants", icon: Truck, to: "/dashboard/soustraitants", def: S, show: tab("soustraitants") },
  { key: "comptes_clients", label: "Comptes clients", icon: Users, to: "/dashboard/comptes-clients", def: S, show: mgr },
  { key: "suivi", label: "Suivi & Traçabilité", icon: ScrollText, to: "/dashboard/suivi", def: S, show: tab("suivi") },
  { key: "journal_emails", label: "Journal des emails", icon: Mail, to: "/dashboard/journal-emails", def: S, show: am },
  { key: "notifications", label: "Notifications", icon: MessageSquare, to: "/dashboard/notifications", def: S, show: adm },
  { key: "chefs_poste", label: "Chefs de poste", icon: Building2, to: "/dashboard/chefs-poste", def: S, show: adm },
  { key: "assistant", label: "Assistant PACAZUR", icon: Bot, to: "/dashboard/assistant", def: S, show: adm },
];

const TABS = [{ key: E, label: "Exploitation", icon: HardHat }, { key: S, label: "Secrétariat", icon: Briefcase }];
let _placementCache = null;

function Tile({ t, i, canMove, onMove, other }) {
  const navigate = useNavigate();
  return (
    <div className="relative group fade-up" style={{ animationDelay: `${i * 30}ms` }}>
      <button type="button" onClick={() => navigate(t.to)} data-testid={`quick-${t.key}`}
        className="w-full h-full flex flex-col items-center justify-center gap-3 text-center border border-white/10 rounded-xl bg-[#121212] px-4 py-7 hover:border-[#007AFF]/50 hover:bg-[#007AFF]/5 hover:-translate-y-0.5 transition-[transform,background-color,border-color] duration-200">
        <span className="w-14 h-14 rounded-2xl bg-[#007AFF]/10 flex items-center justify-center group-hover:bg-[#007AFF]/20 transition-colors">
          <t.icon className="w-7 h-7 text-[#4da3ff]" />
        </span>
        <span className="text-sm font-medium text-zinc-200 group-hover:text-white transition-colors">{t.label}</span>
      </button>
      {canMove && (
        <button type="button" onClick={() => onMove(t)} title={`Déplacer vers ${other}`} aria-label={`Déplacer ${t.label} vers ${other}`} data-testid={`tile-move-${t.key}`}
          className="absolute top-2 right-2 w-7 h-7 rounded-md flex items-center justify-center text-zinc-500 opacity-60 sm:opacity-0 group-hover:opacity-100 focus:opacity-100 hover:text-[#4da3ff] hover:bg-[#007AFF]/10 transition-[opacity,color,background-color]">
          <ArrowLeftRight className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

const Grid = ({ children }) => <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">{children}</div>;

export function ModuleTabs({ user }) {
  const [placement, setPlacement] = useState(_placementCache || {});
  const [active, setActive] = useState(() => localStorage.getItem("dash_tiles_tab") || E);
  const role = user?.role;
  const isAdmin = role === "admin";
  const isManager = ["manager", "admin", "demo"].includes(role);
  const tabs = user?.tab_permissions;
  const ctx = { role, isAdmin, isManager, secOk: role !== "manager" || !!user?.acces_secretariat, canTab: (k) => isAdmin || (isManager ? (Array.isArray(tabs) ? tabs.includes(k) : true) : false) };
  const canMove = isAdmin || role === "manager";

  useEffect(() => {
    api.get("/dashboard/tiles").then(({ data }) => { _placementCache = data.placement || {}; setPlacement(_placementCache); }).catch(() => {});
  }, [user?.tenant_id]);

  const where = (t) => placement[t.key] || t.def;
  const expOk = role !== "manager" || user?.acces_exploitation !== false;
  const shownTabs = TABS.filter((x) => (x.key === S ? ctx.secOk : x.key === E ? expOk : true));
  const cur = shownTabs.some((x) => x.key === active) ? active : shownTabs[0]?.key;
  const visible = TILES.filter((t) => t.show(ctx) && (expOk || !EXP_KEYS.has(t.key)));
  const count = (k) => visible.filter((t) => where(t) === k).length;
  const pick = (k) => { setActive(k); localStorage.setItem("dash_tiles_tab", k); };
  const move = async (t) => {
    const to = where(t) === E ? S : E;
    const prev = placement;
    setPlacement({ ...placement, [t.key]: to });
    try {
      const { data } = await api.put("/dashboard/tiles", { key: t.key, tab: to });
      _placementCache = data.placement; setPlacement(data.placement);
      toast.success(`« ${t.label} » déplacé vers ${TABS.find((x) => x.key === to).label}`);
    } catch (e) { setPlacement(prev); toast.error(e.response?.data?.detail || "Déplacement impossible"); }
  };

  if (!cur) return null;
  const items = visible.filter((t) => where(t) === cur);
  const main = items.filter((t) => !t.group);
  const groups = [...new Set(items.filter((t) => t.group).map((t) => t.group))];
  const other = TABS.find((x) => x.key !== cur).label;
  const mv = canMove && shownTabs.length > 1;

  return (
    <div className="mb-10" data-testid="dash-quick-access">
      <div className="flex gap-1 sm:gap-2 mb-5 border-b border-white/10 overflow-x-auto" role="tablist">
        {shownTabs.map((x) => (
          <button key={x.key} type="button" role="tab" aria-selected={cur === x.key} onClick={() => pick(x.key)} data-testid={`dash-tab-${x.key}`}
            className={`flex items-center gap-2 px-3 sm:px-5 py-3 -mb-px whitespace-nowrap border-b-2 font-display text-base sm:text-lg font-medium transition-colors ${cur === x.key ? "border-[#007AFF] text-white" : "border-transparent text-zinc-500 hover:text-zinc-200"}`}>
            <x.icon className="w-5 h-5 hidden sm:block" /> {x.label}
            <span className="text-xs font-code rounded-full bg-white/5 px-2 py-0.5" data-testid={`dash-tab-count-${x.key}`}>{count(x.key)}</span>
          </button>
        ))}
      </div>
      {items.length === 0 && <p className="text-sm text-zinc-500 py-6" data-testid="dash-tab-empty">Aucun module dans cet onglet.</p>}
      {main.length > 0 && <Grid>{main.map((t, i) => <Tile key={t.key} t={t} i={i} canMove={mv} onMove={move} other={other} />)}</Grid>}
      {groups.map((g) => (
        <div key={g} className="mt-6" data-testid={`dash-group-${g.toLowerCase()}`}>
          <p className="text-xs uppercase tracking-widest text-[#FFB020] font-code mb-3">{g}</p>
          <Grid>{items.filter((t) => t.group === g).map((t, i) => <Tile key={t.key} t={t} i={i} canMove={mv} onMove={move} other={other} />)}</Grid>
        </div>
      ))}
    </div>
  );
}
